import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Analytics } from '@vercel/analytics/react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { plannerTheme } from './theme';
import { surfaceBackgroundSx } from './theme/backgrounds';
import { queryClient, persister } from './utils/queryClient';
import { ApiLimitProvider, useApiLimit } from './contexts/ApiLimitContext';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { LandingPage } from './pages/LandingPage';
import { PlannerInputPage } from './pages/PlannerInputPage';
import { PlannerResultsPage } from './pages/PlannerResultsPage';
import { SearchHistoryPage } from './pages/SearchHistoryPage';
import { SharedTripPage } from './pages/SharedTripPage';
import { PlannerProvider, usePlanner } from './contexts/PlannerContext';
import { AdminApprovalProvider } from './components/auth/AdminApprovalProvider';
import { ClerkAuthProvider } from './components/auth/ClerkAuthProvider';
import { SignOutHandler } from './components/auth/SignOutHandler';
import { UserDashboard } from './components/auth/UserDashboard';
import { AdminDashboard } from './components/auth/AdminDashboard';
import { ApiLimitReached } from './components/auth/ApiLimitReached';
import { ContactDrawer } from './components/shell/ContactDrawer';
import { PeriploNotice } from './components/PeriploNotice';
import { AssistantLauncher } from './components/planner/AssistantLauncher';
import { AssistantDrawer } from './components/planner/AssistantDrawer';

// Lazy-loaded pages (code-split to reduce initial bundle size)
const FAQPage = React.lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const BlogListPage = React.lazy(() => import('./pages/BlogListPage').then(m => ({ default: m.BlogListPage })));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const DestinationPage = React.lazy(() => import('./pages/DestinationPage').then(m => ({ default: m.DestinationPage })));
const DestinationsPage = React.lazy(() => import('./pages/DestinationsPage').then(m => ({ default: m.DestinationsPage })));

function ScrollToTop() {
  const { pathname } = useLocation();
  const isTripPage = pathname.startsWith('/trip');

  React.useEffect(() => {
    if (isTripPage) return; // Keep scroll position on trip pages while plans update
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [isTripPage, pathname]);

  return null;
}

function App() {
  React.useEffect(() => {
    const setViewportDimensions = () => {
      const vh = window.innerHeight * 0.01;
      const vw = window.innerWidth * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      document.documentElement.style.setProperty('--vw', `${vw}px`);
    };

    // Set initial dimensions
    setViewportDimensions();

    // Update on resize (handles address bar show/hide)
    window.addEventListener('resize', setViewportDimensions);

    // Update on orientation change
    window.addEventListener('orientationchange', () => {
      setTimeout(setViewportDimensions, 100);
    });

    return () => {
      window.removeEventListener('resize', setViewportDimensions);
      window.removeEventListener('orientationchange', setViewportDimensions);
    };
  }, []);

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
      <ThemeProvider theme={plannerTheme}>
        <CssBaseline enableColorScheme />
        <Router>
          <ClerkAuthProvider>
            <ApiLimitProvider>
              <ScrollToTop />
              <AppShell />
              <Analytics />
            </ApiLimitProvider>
          </ClerkAuthProvider>
        </Router>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
}

// True when the custom prerender script is capturing static HTML
const isPrerender = typeof window !== 'undefined' && (window as any).__PRERENDER;

function AppShell() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { redirectToSignIn } = useClerk();
  const [contactOpen, setContactOpen] = React.useState(false);
  const location = useLocation();
  const isChatPage = location.pathname === '/chat';

  if (!isLoaded && !isPrerender) {
    return (
      <Box
        sx={{
          minHeight: { xs: 'calc(var(--vh, 1vh) * 100)', sm: '100vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(255, 56, 92, 0.08) 0%, rgba(255, 255, 255, 1) 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: 'primary.main', mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Warming up your planner…
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleSignIn = () => {
    redirectToSignIn({ redirectUrl: window.location.href });
  };

  return (
    <PlannerProvider>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: { xs: 'calc(var(--vh, 1vh) * 100)', sm: '100vh' },
      }}>
        <SignOutHandler />
        <Navigation
          isSignedIn={isSignedIn ?? false}
          userName={user?.firstName || undefined}
          onSignIn={handleSignIn}
          onContactToggle={() => setContactOpen(true)}
        />

        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            mt: { xs: '56px', sm: '64px' },
            overflow: 'visible',
            position: 'relative',
            minHeight: {
              xs: 'calc((var(--vh, 1vh) * 100) - 56px)',
              sm: 'calc(100vh - 64px)'
            },
            ...surfaceBackgroundSx(theme),
          })}
        >
          <PeriploNotice />
          <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress sx={{ color: 'primary.main' }} /></Box>}>
          <Routes>
            <Route path="/" element={<PlannerInputRoute />} />
            <Route path="/chat" element={<LandingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/plan/:location/:dayBegin/:dayEnd" element={<PlannerResultsRoute />} />
            <Route path="/planner" element={<Navigate to="/" replace />} />
            <Route path="/history" element={<SearchHistoryPage />} />
            <Route path="/trip" element={<PlannerResultsRoute />} />
            <Route path="/trip/:searchId" element={<SharedTripPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/destinations" element={<DestinationsPage />} />
            <Route path="/itinerary/:slug" element={<DestinationPage />} />
            <Route
              path="/dashboard"
              element={isSignedIn ? <UserDashboard /> : <Navigate to="/" replace />}
            />
            <Route
              path="/admin"
              element={isSignedIn ? (
                <AdminApprovalProvider>
                  <AdminDashboard />
                </AdminApprovalProvider>
              ) : (
                <Navigate to="/" replace />
              )}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </React.Suspense>
          <GlobalAssistant />
        </Box>

        {!isChatPage && <Footer />}
        <ContactDrawer open={contactOpen} onClose={() => setContactOpen(false)} />
      </Box>
    </PlannerProvider>
  );
}

function GlobalAssistant() {
  const { travelData, activeExperience } = usePlanner();
  const [assistantOpen, setAssistantOpen] = React.useState(false);
  const location = useLocation();
  const isChatPage = location.pathname === '/chat';

  // Close assistant when navigating to a different page
  React.useEffect(() => {
    setAssistantOpen(false);
  }, [location.pathname]);

  // Only show assistant if there's travel data
  if (isChatPage || !travelData || !travelData.destination) {
    return null;
  }

  return (
    <>
      <AssistantLauncher
        onClick={() => setAssistantOpen(true)}
        hidden={Boolean(activeExperience) || assistantOpen}
      />
      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </>
  );
}

const PlannerInputRoute: React.FC = () => {
  const { isSignedIn } = useAuth();
  const { limitReached } = useApiLimit();

  if (!isSignedIn && limitReached) {
    return <ApiLimitReached />;
  }

  if (!isSignedIn) {
    return (
      <Box sx={{ pb: 12 }}>
        <PlannerInputPage />
      </Box>
    );
  }

  return (
    <AdminApprovalProvider>
      <PlannerInputPage />
    </AdminApprovalProvider>
  );
};

const PlannerResultsRoute: React.FC = () => {
  const { isSignedIn } = useAuth();
  const { limitReached } = useApiLimit();

  if (!isSignedIn && limitReached) {
    return <ApiLimitReached />;
  }

  if (!isSignedIn) {
    return (
      <Box sx={{ pb: 12 }}>
        <PlannerResultsPage />
      </Box>
    );
  }

  return (
    <AdminApprovalProvider>
      <PlannerResultsPage />
    </AdminApprovalProvider>
  );
};

export default App;
